import { useState, useEffect, useRef } from 'react'
import { useSearchParams } from 'react-router-dom'
import api from '../lib/api'
import { useAuth } from '../context/AuthContext'
import { notifyExternally } from '../context/ToastContext'

// /account/restore — fullscreen state for soft-deleted accounts that just
// signed back in. Auth middleware emits 403 PENDING_DELETION with
// pendingDeletionAt; the global interceptor redirects here.
//
// Frozen/banned accounts that try to restore are refused by the server
// (POST /api/user/restore returns 403 FROZEN or 403 BANNED). The global
// interceptor catches those and routes to / (SuspensionGate) or /login
// (banned banner). This page only renders the success path.
//
// Visual aesthetic mirrors SuspensionGate — reuses the same CSS classes.
export default function AccountRestoration() {
  const [searchParams] = useSearchParams()
  const { signOut } = useAuth()
  const [pendingDeletionAt, setPendingDeletionAt] = useState(searchParams.get('pendingDeletionAt') || null)
  const [scheduledHardDeleteAt, setScheduledHardDeleteAt] = useState(null)
  const [remaining, setRemaining] = useState('')
  const [restoring, setRestoring] = useState(false)
  const [checked, setChecked] = useState(false)
  const timerRef = useRef(null)

  // Fetch the canonical timestamps. If the server says the account is not
  // pending deletion (already restored, or never was), redirect home — this
  // page must not render restoration UI for an account that isn't pending.
  useEffect(() => {
    let cancelled = false
    ;(async () => {
      try {
        const { data } = await api.get('/api/user/pending-deletion-status')
        if (cancelled) return
        if (data?.pendingDeletionAt) {
          setPendingDeletionAt(data.pendingDeletionAt)
          setScheduledHardDeleteAt(data.scheduledHardDeleteAt)
          setChecked(true)
        } else {
          window.location.assign('/')
        }
      } catch {
        // 403 PENDING_DELETION re-fires from the interceptor → already on this
        // page; other failures are silent and the countdown just won't render.
        if (!cancelled) setChecked(true)
      }
    })()
    return () => { cancelled = true }
  }, [])

  // Derive scheduledHardDeleteAt from the URL-provided pendingDeletionAt if
  // the fetch hasn't returned yet.
  useEffect(() => {
    if (scheduledHardDeleteAt || !pendingDeletionAt) return
    const start = new Date(pendingDeletionAt).getTime()
    if (!Number.isFinite(start)) return
    setScheduledHardDeleteAt(new Date(start + 180 * 24 * 60 * 60 * 1000).toISOString())
  }, [pendingDeletionAt, scheduledHardDeleteAt])

  // Countdown tick
  useEffect(() => {
    if (!scheduledHardDeleteAt) return
    function tick() {
      const end = new Date(scheduledHardDeleteAt).getTime()
      const diff = end - Date.now()
      if (diff <= 0) { setRemaining('0d'); return }
      const days = Math.floor(diff / 86400000)
      const hours = Math.floor((diff % 86400000) / 3600000)
      setRemaining(days > 0 ? `${days}d ${hours}h` : `${hours}h`)
    }
    tick()
    timerRef.current = setInterval(tick, 60 * 1000)
    return () => clearInterval(timerRef.current)
  }, [scheduledHardDeleteAt])

  async function handleRestore() {
    setRestoring(true)
    try {
      await api.post('/api/user/restore')
      notifyExternally('Account restored.', 'success', 4000)
      window.location.assign('/')
    } catch (err) {
      // 403 FROZEN / 403 BANNED are handled by the global interceptor (page
      // navigates away). Anything else gets a generic toast.
      const code = err?.response?.data?.code
      if (code !== 'FROZEN' && code !== 'BANNED' && code !== 'PENDING_DELETION') {
        notifyExternally('Could not restore your account. Please try again.', 'error', 5000)
        setRestoring(false)
      }
    }
  }

  async function handleSignOut() {
    try { await signOut() } catch {}
    window.location.assign('/')
  }

  if (!checked) return null

  return (
    <div className="suspension-gate">
      <div className="suspension-gate__card">
        <div className="suspension-gate__icon">⏳</div>
        <h1 className="suspension-gate__title">Welcome back.</h1>
        <p className="suspension-gate__desc">
          Your account is scheduled for deletion.
        </p>
        {scheduledHardDeleteAt && (
          <div className="suspension-gate__timer">
            <div className="suspension-gate__timer-label">Time remaining to restore</div>
            <div className="suspension-gate__timer-value">{remaining || '—'}</div>
          </div>
        )}
        <p className="suspension-gate__note">
          Restoring will bring back your profile and content as they were. Verse
          coins forfeit at deletion are not refunded. Your prior session has
          ended; you are signed in fresh now.
        </p>
        <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'center', marginTop: '1.5rem', flexWrap: 'wrap' }}>
          <button
            onClick={handleRestore}
            disabled={restoring}
            style={{
              padding: '0.75rem 1.5rem',
              background: 'var(--accent-primary)',
              color: 'var(--bg-primary)',
              border: 'none',
              borderRadius: '6px',
              fontWeight: 600,
              cursor: restoring ? 'not-allowed' : 'pointer',
              opacity: restoring ? 0.6 : 1,
            }}
          >
            {restoring ? 'Restoring…' : 'Restore my account'}
          </button>
          <button
            onClick={handleSignOut}
            style={{
              padding: '0.75rem 1.5rem',
              background: 'transparent',
              color: 'var(--text-primary)',
              border: '1px solid var(--border-primary)',
              borderRadius: '6px',
              fontWeight: 500,
              cursor: 'pointer',
            }}
          >
            Sign out — I'll decide later
          </button>
        </div>
        <div className="suspension-gate__legal" style={{ marginTop: '1.5rem' }}>
          <a href="/terms">Terms of Service</a>
          <span> · </span>
          <a href="mailto:support@versesite.com" className="suspension-gate__link">support@versesite.com</a>
        </div>
      </div>
    </div>
  )
}
